import React from "react";

const Alert = ({
  children,
  variant = "info",
  title,
  dismissible = false,
  onDismiss,
  className = "",
  ...props
}) => {
  const variants = {
    info: "bg-[var(--theme-info-bg)] border-[var(--theme-info)] text-[var(--theme-text-primary)]",
    success:
      "bg-[var(--theme-success-bg)] border-[var(--theme-success)] text-[var(--theme-text-primary)]",
    warning:
      "bg-[var(--theme-warning-bg)] border-[var(--theme-warning)] text-[var(--theme-text-primary)]",
    error:
      "bg-[var(--theme-error-bg)] border-[var(--theme-error)] text-[var(--theme-text-primary)]",
  };

  // Add contextual guidance for common errors
  const getContextualGuidance = () => {
    if (typeof children === "string") {
      const errorMessages = {
        "network error": "Please check your internet connection and try again.",
        "invalid credentials": "Please verify your username and password.",
        "permission denied": "Contact your administrator for access rights.",
        "validation failed": "Please check the form for required fields.",
      };

      const lowerCaseMessage = children.toLowerCase();
      for (const [key, guidance] of Object.entries(errorMessages)) {
        if (lowerCaseMessage.includes(key)) {
          return guidance;
        }
      }
    }
    return null;
  };

  const guidance = getContextualGuidance();

  const icons = {
    info: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    ),
    success: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    warning: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    ),
    error: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
  };

  const classes = `border rounded-lg p-4 ${variants[variant]} ${className}`;

  return (
    <div className={classes} {...props}>
      <div className="flex">
        <div className="flex-shrink-0">{icons[variant]}</div>
        <div className="ml-3 flex-1">
          {title && <h3 className="text-sm font-medium">{title}</h3>}
          <div className={`text-sm ${title ? "mt-1" : ""} whitespace-normal`}>
            {children}
          </div>
          {/* Render contextual guidance when available */}
          {guidance && (
            <div className="mt-2 text-sm text-[var(--color-text-secondary)] bg-[var(--color-bg-secondary)] border border-[var(--color-border-default)] rounded p-2">
              <span className="font-medium">Tip: </span>
              {guidance}
            </div>
          )}
        </div>
        {dismissible && onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onDismiss}
                className="inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-primary)] transition-colors"
              >
                <span className="sr-only">Dismiss</span>
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Alert;
